// Personalizer Logic with Interactive Canvas (Enhanced with DPI Validation & Cart Guard)
window.Personalizer = {
    canvas: null,
    ctx: null,
    productImage: null,
    logoImage: null,
    hasSaved: false, // Guard flag for Cart

    // Transform State
    logo: {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        scale: 1,
        rotation: 0 
    },

    // Interaction State
    isDragging: false,
    dragAction: null, 
    lastPos: { x: 0, y: 0 },

    // Config
    handleSize: 10,
    rotateHandleOffset: 30,
    minPixelWidth: 1500, // This represents 300 DPI for a 5-inch print area

    fileInput: null,
    container: null,

    init: function (containerSelector, fileInputSelector) {
        this.container = document.querySelector(containerSelector);
        this.fileInput = document.querySelector(fileInputSelector);

        if (!this.container || !this.fileInput) return;

        this.setupUI();
        this.bindEvents();
        this.setupCartGuard();
    },

    setupUI: function () {
        // Create Modal with Requirement Text
        const modalHTML = `
            <div id="personalizer-modal" style="display:none; position:fixed; top:0; left:0; width:100%; height:100%; z-index:99999; justify-content:center; align-items:center; background: rgba(0,0,0,0.8);">
                <div class="personalizer-modal-content" style="background:white; padding:20px; border-radius:8px; max-width:90%; position:relative; box-shadow: 0 10px 30px rgba(0,0,0,0.5);">
                    <div class="personalizer-modal-header" style="margin-bottom:15px;">
                        <h3 style="margin:0;">Customize Your Design</h3>
                        <p id="dpi-requirement-notice" style="color: #d9534f; font-size: 13px; font-weight: bold; margin: 5px 0 0 0;">
                            ⚠️ Requirement: Minimum 300 DPI (approx. 1500px width)
                        </p>
                        <div class="personalizer-modal-close" id="personalizer-close-x" style="cursor:pointer; position:absolute; right:15px; top:10px; font-size:24px;">&times;</div>
                    </div>
                    
                    <canvas id="personalizer-canvas" width="500" height="500" style="border:1px solid #e1e1e1; background: #fff; touch-action: none; cursor: crosshair; max-width: 100%; height: auto;"></canvas>
                    
                    <div class="personalizer-actions" style="margin-top:15px; display:flex; gap:10px; justify-content: flex-end;">
                        <button id="personalizer-cancel" class="personalizer-btn btn-cancel" style="padding:10px 20px; border:1px solid #ccc; background:#fff; cursor:pointer;">Cancel</button>
                        <button id="personalizer-save" class="personalizer-btn btn-save" style="background:#000; color:#fff; padding:10px 20px; border:none; cursor:pointer; font-weight:bold;">Save Design</button>
                    </div>
                </div>
            </div>
        `;

        const existing = document.getElementById('personalizer-modal');
        if (existing) existing.remove();

        document.body.insertAdjacentHTML('beforeend', modalHTML);

        this.canvas = document.getElementById('personalizer-canvas');
        this.ctx = this.canvas.getContext('2d');

        // Add Requirement label below the file input
        const info = document.createElement('div');
        info.innerHTML = `<small style="color: #d9534f; display:block; margin-top:5px;">Minimum 300 DPI images only (1500px+ width recommended).</small>`;
        this.fileInput.parentNode.insertBefore(info, this.fileInput.nextSibling);
    },

    bindEvents: function () {
        const _this = this;

        // File Upload with DPI Validation
        this.fileInput.addEventListener('change', function (e) {
            if (e.target.files && e.target.files[0]) {
                _this.loadLogo(e.target.files[0]);
            }
        });

        // Trigger Button
        const triggerBtn = document.getElementById('personalizer-trigger-btn');
        if (triggerBtn) {
            triggerBtn.addEventListener('click', function (e) {
                e.preventDefault();
                if (_this.logoImage) {
                    document.getElementById('personalizer-modal').style.display = 'flex';
                    _this.draw(true);
                } else {
                    alert("Please upload a logo first.");
                }
            });
        }

        // Canvas Controls
        this.canvas.addEventListener('mousedown', this.handleStart.bind(this));
        this.canvas.addEventListener('touchstart', this.handleStart.bind(this), { passive: false });
        window.addEventListener('mousemove', this.handleMove.bind(this));
        window.addEventListener('touchmove', this.handleMove.bind(this), { passive: false });
        window.addEventListener('mouseup', this.handleEnd.bind(this));
        window.addEventListener('touchend', this.handleEnd.bind(this));

        // Modal Controls
        const closeModal = (e) => {
            e.preventDefault();
            document.getElementById('personalizer-modal').style.display = 'none';
        };
        document.getElementById('personalizer-cancel').addEventListener('click', closeModal);
        document.getElementById('personalizer-close-x').addEventListener('click', closeModal);

        document.getElementById('personalizer-save').addEventListener('click', function (e) {
            e.preventDefault();
            _this.save();
        });
    },

    setupCartGuard: function () {
        const _this = this;
        // Detect most common Add to Cart button selectors
        const addToCartBtn = document.querySelector('form[action="/cart/add"] [type="submit"], .product-form__submit, #AddToCart, button[name="add"]');

        if (addToCartBtn) {
            addToCartBtn.addEventListener('click', function (e) {
                if (!_this.hasSaved) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    alert("⚠️ ACTION REQUIRED: You must upload a high-resolution logo (300 DPI) and click 'Save Design' before adding this item to your cart.");
                    
                    // Visual feedback: scroll to upload
                    _this.fileInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    _this.fileInput.style.boxShadow = "0 0 10px red";
                    setTimeout(() => { _this.fileInput.style.boxShadow = ""; }, 2000);
                }
            }, true); // Use capture phase to ensure we intercept other scripts
        }
    },

    loadLogo: function (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                // --- DPI / RESOLUTION VALIDATION ---
                if (img.width < this.minPixelWidth) {
                    alert(`❌ Image quality too low.\n\nYour image is ${img.width}px wide. For 300 DPI printing, your logo must be at least ${this.minPixelWidth}px wide. Please upload a higher resolution file.`);
                    this.fileInput.value = ""; // Reset file input
                    return;
                }

                this.logoImage = img;
                this.hasSaved = false; // Reset save status on new upload

                // Initialize Position (Center)
                this.logo.width = img.width;
                this.logo.height = img.height;
                const targetWidth = this.canvas.width * 0.4;
                this.logo.scale = targetWidth / this.logo.width;
                this.logo.x = this.canvas.width / 2;
                this.logo.y = this.canvas.height / 2;
                this.logo.rotation = 0;

                // Load Base Product Image
                const mainImg = document.querySelector('.product-gallery__image img') || document.querySelector('.product__image-Container img');
                if (mainImg) {
                    const base = new Image();
                    base.crossOrigin = "Anonymous";
                    base.onload = () => {
                        this.productImage = base;
                        this.draw(true);
                    };
                    base.src = mainImg.currentSrc || mainImg.src;
                } else {
                    this.draw(true);
                }

                document.getElementById('personalizer-modal').style.display = 'flex';
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    },

    draw: function (showControls = true) {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        if (this.productImage) {
            const scale = Math.min(this.canvas.width / this.productImage.width, this.canvas.height / this.productImage.height);
            const x = (this.canvas.width / 2) - (this.productImage.width / 2) * scale;
            const y = (this.canvas.height / 2) - (this.productImage.height / 2) * scale;
            this.ctx.drawImage(this.productImage, x, y, this.productImage.width * scale, this.productImage.height * scale);
        }

        if (this.logoImage) {
            this.ctx.save();
            this.ctx.translate(this.logo.x, this.logo.y);
            this.ctx.rotate(this.logo.rotation * Math.PI / 180);
            this.ctx.scale(this.logo.scale, this.logo.scale);
            this.ctx.drawImage(this.logoImage, -this.logo.width / 2, -this.logo.height / 2);
            this.ctx.restore();

            if (showControls) this.drawControls();
        }
    },

    drawControls: function () {
        this.ctx.save();
        this.ctx.translate(this.logo.x, this.logo.y);
        this.ctx.rotate(this.logo.rotation * Math.PI / 180);
        const w = this.logo.width * this.logo.scale;
        const h = this.logo.height * this.logo.scale;
        const halfW = w / 2;
        const halfH = h / 2;

        this.ctx.strokeStyle = '#00aaff';
        this.ctx.lineWidth = 2;
        this.ctx.strokeRect(-halfW, -halfH, w, h);

        this.ctx.beginPath();
        this.ctx.moveTo(0, -halfH);
        this.ctx.lineTo(0, -halfH - this.rotateHandleOffset);
        this.ctx.stroke();

        this.ctx.fillStyle = '#fff';
        this.ctx.strokeStyle = '#00aaff';
        
        // Rotate Handle
        this.ctx.beginPath();
        this.ctx.arc(0, -halfH - this.rotateHandleOffset, this.handleSize, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.stroke();

        // Scale Handles
        [ { x: -halfW, y: -halfH }, { x: halfW, y: -halfH }, { x: -halfW, y: halfH }, { x: halfW, y: halfH } ].forEach(h => {
            this.ctx.beginPath();
            this.ctx.rect(h.x - this.handleSize / 2, h.y - this.handleSize / 2, this.handleSize, this.handleSize);
            this.ctx.fill();
            this.ctx.stroke();
        });
        this.ctx.restore();
    },

    getMousePos: function (e) {
        const rect = this.canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return { x: clientX - rect.left, y: clientY - rect.top };
    },

    toLocalSpace: function (point) {
        const dx = point.x - this.logo.x;
        const dy = point.y - this.logo.y;
        const rad = -this.logo.rotation * Math.PI / 180;
        return {
            x: dx * Math.cos(rad) - dy * Math.sin(rad),
            y: dx * Math.sin(rad) + dy * Math.cos(rad)
        };
    },

    handleStart: function (e) {
        if (e.type === 'touchstart') e.preventDefault();
        const pos = this.getMousePos(e);
        this.lastPos = pos;
        const local = this.toLocalSpace(pos);
        const w = this.logo.width * this.logo.scale;
        const h = this.logo.height * this.logo.scale;
        const halfW = w / 2; const halfH = h / 2;
        const hit = this.handleSize * 1.5;

        this.dragAction = null;
        const rotY = -halfH - this.rotateHandleOffset;

        if (Math.abs(local.x) < hit && Math.abs(local.y - rotY) < hit) this.dragAction = 'rotate';
        else if (Math.abs(local.x - (-halfW)) < hit && Math.abs(local.y - (-halfH)) < hit) this.dragAction = 'scale-tl';
        else if (Math.abs(local.x - (halfW)) < hit && Math.abs(local.y - (-halfH)) < hit) this.dragAction = 'scale-tr';
        else if (Math.abs(local.x - (-halfW)) < hit && Math.abs(local.y - (halfH)) < hit) this.dragAction = 'scale-bl';
        else if (Math.abs(local.x - (halfW)) < hit && Math.abs(local.y - (halfH)) < hit) this.dragAction = 'scale-br';
        else if (local.x > -halfW && local.x < halfW && local.y > -halfH && local.y < halfH) this.dragAction = 'move';

        if (this.dragAction) this.isDragging = true;
    },

    handleMove: function (e) {
        if (!this.isDragging) return;
        const pos = this.getMousePos(e);
        const dx = pos.x - this.lastPos.x;
        const dy = pos.y - this.lastPos.y;

        if (this.dragAction === 'move') {
            this.logo.x += dx;
            this.logo.y += dy;
        } else if (this.dragAction === 'rotate') {
            const angle = Math.atan2(pos.y - this.logo.y, pos.x - this.logo.x);
            this.logo.rotation = angle * 180 / Math.PI + 90;
        } else if (this.dragAction.startsWith('scale')) {
            const currentDist = Math.hypot(pos.x - this.logo.x, pos.y - this.logo.y);
            const lastDist = Math.hypot(this.lastPos.x - this.logo.x, this.lastPos.y - this.logo.y);
            this.logo.scale *= (currentDist / lastDist);
        }
        this.lastPos = pos;
        this.draw(true);
    },

    handleEnd: function () { this.isDragging = false; this.dragAction = null; },

    save: function () {
        this.draw(false);
        const _this = this;
        
        const coordsInput = document.querySelector('input[name="properties[_Placement Data]"]');
        if (coordsInput) coordsInput.value = JSON.stringify(this.logo);

        this.canvas.toBlob(function (blob) {
            window._generatedPreviewBlob = blob;
            _this.hasSaved = true; // Unlock the Cart

            const btn = document.getElementById('personalizer-save');
            const originalText = btn.innerText;
            btn.innerText = "✓ Saved Successfully";
            btn.style.background = "#4CAF50";

            setTimeout(() => {
                document.getElementById('personalizer-modal').style.display = 'none';
                btn.innerText = originalText;
                btn.style.background = "";

                const previewContainer = document.getElementById('personalizer-preview-container');
                if (previewContainer) {
                    previewContainer.innerHTML = '<strong>Design Ready</strong>';
                    const img = document.createElement('img');
                    img.src = URL.createObjectURL(blob);
                    img.style.maxWidth = '100px';
                    img.style.display = 'block';
                    previewContainer.appendChild(img);
                }
            }, 1000);
        }, 'image/png');
    }
};